import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Input, Tab } from 'semantic-ui-react';
import i18n from '~/components/i18n';
import { useDebounce } from 'use-lodash-debounce';
import { UsersQuery } from '~/QueryHooks/UsersQuery';
import notify from '~/components/Notify';
import CreateEditUser from './CreateEditUser';
import UsersService from '~/api/services/users';
import TableComponent from '~/components/TableComponent';
import { useAuth } from '~/routes/components/AuthProvider';
import { ADMIN_ROLE_SLUG } from '~/Constants';
import { El as LayoutEl } from '~/components/Layout/style';
import Layout from '~/components/Layout';
import Dialog from '~/components/Dialog';
import FarmerGroupQuery from '~/QueryHooks/FarmerGroupsQuery';
import ShowUser from './ShowUser';
import { handleErrors } from '~/utils/helpers';

const getDefaultTableColumnHeaders = () => [
  {
    name: i18n.t('Name'),
    key: 'name',
    sorted: 'asc'
  },
  {
    name: i18n.t('Email'),
    key: 'email',
    sorted: 'desc'
  },
  {
    name: i18n.t('Role'),
    key: 'roleLabel'
  },
  {
    name: i18n.t('Verified'),
    key: 'verified',
    type: 'verified',
    sorted: 'desc'
  },
  {
    name: i18n.t('Actions')
  }
];

const getActions = () => [
  {
    popupMessage: i18n.t('Edit'),
    type: 'icon',
    name: 'pencil',
    size: 'large',
    actionType: 'edit'
  },
  {
    popupMessage: i18n.t('Delete'),
    type: 'icon',
    name: 'trash',
    size: 'large',
    actionType: 'delete',
    verifiedAction: 0,
    verifiedActionKey: 'verified'
  },
  {
    popupMessage: i18n.t('Send password reset link'),
    type: 'icon',
    name: 'mail',
    size: 'large',
    actionType: 'mail',
    verifiedAction: 1,
    verifiedActionKey: 'verified'
  },
  {
    popupMessage: i18n.t('Resend password reset link'),
    type: 'icon',
    name: 'linkify',
    size: 'large',
    actionType: 'resend',
    verifiedAction: 0,
    verifiedActionKey: 'verified'
  },
  {
    popupMessage: i18n.t('Unblock'),
    type: 'icon',
    name: 'checkmark',
    size: 'large',
    color: 'green',
    actionType: 'blockUnblock',
    display: [0]
  },
  {
    popupMessage: i18n.t('Block'),
    type: 'icon',
    name: 'close',
    size: 'large',
    color: 'red',
    actionType: 'blockUnblock',
    display: [1]
  }
]

const getViewAction = () => [
  {
    popupMessage: i18n.t('Show more'),
    type: 'icon',
    name: 'eye',
    size: 'large',
    actionType: 'show'
  }
]

const DairyFactoryProfileUsers: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const [isBusyDelete, setIsBusyDelete] = useState(false);
  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 10,
    search: '',
    status: 1,
    column: '',
    sort: ''
  });
  const [blockUser, setBlockUser] = useState(null)
  const [drawer, setDrawerState] = useState({
    isVisible: false,
    id: null
  });
  const { systemData: { logged: { roleSlug } } } = useAuth()
  const [deleteUser, setDeleteUser] = useState(null);
  const [search, setSearch] = useState('');
  const debouncedValue = useDebounce(search, 300);
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getDefaultTableColumnHeaders())
  const adminLogged = roleSlug === ADMIN_ROLE_SLUG
  const [showUserId, setShowUserId] = useState(null)

  const {
    isLoading: isBusyOptions,
    data: usersOptions,
    //@ts-ignore
    refetch: fetchUserOptions
  } = adminLogged ? UsersQuery.getUserOptionsData() : { isLoading: false, data: null }

  const {
    data: usersData,
    isLoading: isBusy,
    isFetching: isFetching,
    refetch: fetchFilteredData
  } = UsersQuery.getUsersList({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  });

  useEffect(() => {
    setTableColumnHeaders(getDefaultTableColumnHeaders())
    fetchFilteredData();
    if (adminLogged) {
      fetchUserOptions()
    }
  }, [t])

  const onBlockUserSuccess = () => {
    notify(t('User successfully blocked'), 'success');
    fetchFilteredData();
  };

  const onUnblockUserSuccess = () => {
    notify(t('User successfully unblocked'), 'success');
    fetchFilteredData();
  };

  const { isLoading: isBlockUserLoading, mutate: blockUserById } = UsersQuery.blockUserById(
    onBlockUserSuccess
  );

  const { isLoading: isUnblockUserLoading, mutate: unblockUserById } = UsersQuery.unblockUserById(
    onUnblockUserSuccess
  );

  const onSendPasswordResetLinkSuccess = () => {
    notify(t('Password reset link successfully sent to user'), 'success');
  };

  const { isLoading: isSendPasswordResetLinkLoading, mutate: sendPasswordResetLinkToUserById } = UsersQuery.sendPasswordResetLinkToUserById(
    onSendPasswordResetLinkSuccess
  );

  const {
    data: farmerGroups,
    isLoading: isBusyFarmerGroups
  } = FarmerGroupQuery.getFarmersListOrganization({ isOrganization: 1 });

  useEffect(() => {
    if (dataFiltered) {
      fetchFilteredData();
    }
  }, [dataFiltered]);

  useEffect(() => {
    setDataFiltered({ ...dataFiltered, search: debouncedValue, page: 0 });
  }, [debouncedValue]);

  async function resendPaswordResetLink(id) {
    try {
      await UsersService.resendActivation(id)
      notify(t('Successfully sent reset link'), 'success')
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
  }

  function handleActionClick(row, type) {
    switch (type) {
      case 'show':
        return setShowUserId(row.id)
      case 'edit':
        return setDrawerState({ isVisible: true, id: row.id })
      case 'delete':
        return setDeleteUser(row)
      case 'resend':
        return resendPaswordResetLink(row.id)
      case 'blockUnblock':
        return setBlockUser(row)
      case 'mail':
        return sendPasswordResetLinkToUserById(row.id)
      default:
        return
    }
  }

  const handleBlockUser = (user) => {
    if (user.status === 1) {
      blockUserById(user.id);
    } else if (user.status === 0) {
      unblockUserById(user.id);
    }
    setBlockUser(null)
  };

  function renderBlockModal() {
    const action = blockUser.status === 1 ? t('Block') : t('Unblock')
    const name = blockUser?.name
    return <Dialog
      isDialogOpen={Boolean(blockUser)}
      textClose={t('Close')}
      handleClose={() => setBlockUser(null)}
      textSubmit={t(`${blockUser.status === 1 ? 'Block' : 'Unblock'}`)}
      handleSubmit={() => handleBlockUser(blockUser)}
      textContent={t('Do you want to {{action}} {{name}}?', { action, name })}
      textHeader={t('{{action}} {{name}}', { action, name })}
      type={'abort'}
    />
  }

  function renderContent() {
    return <TableComponent
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      rows={rows}
      isFetchingData={isFetching}
      animated
      actions={adminLogged ? getActions() : getViewAction()}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      total={filtered}
      handleChangeDataFiltered={(data, tableColumnHeaders) => {
        setTableColumnHeaders(tableColumnHeaders)
        setDataFiltered(data)
      }}
    />
  }

  function renderSearch() {
    return <Input
      icon={{ name: 'search', circular: true, link: true }}
      placeholder={t('Search...')}
      value={search}
      style={{ marginRight: 10 }}
      onChange={(e, data) => setSearch(data.value)}
    />
  }

  function renderActions() {
    return adminLogged && <LayoutEl.PageHeaderActionButton
      onClick={() => setDrawerState({
        isVisible: true,
        id: 'create'
      })}
    >{t('Add new user')}</LayoutEl.PageHeaderActionButton>
  }

  function handleTabChange(activeIndex) {
    setDataFiltered({
      ...dataFiltered,
      status: activeIndex === 0 ? 1 : 0,
      page: 0
    });
  }

  function renderPanes() {
    const panes = [
      {
        menuItem: t('Active users'),
        render: () => renderContent()
      },
      {
        menuItem: t('Deactivated users'),
        render: () => renderContent()
      }
    ];

    return (
      <Tab
        menu={{ secondary: true, pointing: true }}
        panes={panes}
        onTabChange={(_, data) => handleTabChange(data.activeIndex)}
      />
    );
  }

  async function handleDeleteUser() {
    setIsBusyDelete(true);
    try {
      await UsersService.deleteUser(deleteUser.id);
      notify(t('User deleted'), 'success');
      setDeleteUser(null);
      fetchFilteredData();
    } catch (error) {
      handleErrors(error)
    }
    setIsBusyDelete(false);
  }

  function renderDeleteModal() {
    const name = deleteUser?.name

    return <Dialog
      isDialogOpen={Boolean(deleteUser)}
      textClose={t('No')}
      isLoading={isBusyDelete}
      handleClose={() => setDeleteUser(null)}
      textSubmit={t('Yes')}
      handleSubmit={() => handleDeleteUser()}
      textContent={t('Do you want to delete {{name}} ?', { name })}
      textHeader={t('Delete {{name}} ?', { name })}
      type={'abort'}
    />
  }

  const { filtered, rows } = usersData ? usersData : { filtered: 0, rows: [] };

  return (
    <Layout
      isBusy={isBusy || isBusyOptions || isBusyFarmerGroups}
      title={t('Dairy Users')}
      filters={renderSearch()}
      useSpinner
      childContainerSize={'300px'}
      fontSizeHeader={'18px'}
      actions={renderActions()}
    >
      {deleteUser && renderDeleteModal()}
      {usersData && renderPanes()}
      {Boolean(blockUser) && renderBlockModal()}
      <CreateEditUser
        isVisible={drawer.isVisible}
        roleOptions={usersOptions?.roles.filter(role => role.slug !== 'user') || []}
        countries={usersOptions?.countries || []}
        farmerGroups={farmerGroups?.map(farmerGroup => ({ value: farmerGroup.id, text: farmerGroup.name }))}
        handleHiddenDrawer={() => {
          setDrawerState({ isVisible: false, id: null });
          fetchFilteredData();
        }}
        userId={drawer.id}
      />
      <ShowUser
        userId={showUserId}
        handleHiddenDrawer={() => setShowUserId(null)}
      />
    </Layout>
  );
};

export default DairyFactoryProfileUsers;
